import React, {useState, useEffect, useMemo} from 'react';
import {
    Button,
    Table,
    Input,
    Form,
    Select,
    Typography,
    message,
    Layout,
    Row,
    Col,
    Card,
    Space,
    Tag,
    Modal,
    Dropdown,
    Radio, RadioChangeEvent
} from 'antd';
import {
    SearchOutlined,
    DeleteOutlined,
    EyeOutlined,
    PlusOutlined,
    DownOutlined,
    PrinterOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import {baseDataService, membershipService, useAuth} from '../../services';
import MembershipPdfViewer from './components/MembershipPdfViewer';
import {useLocation, useNavigate} from 'react-router-dom';
import {
    MemSearchRequest,
    MemSearchResponse,
    MemberCreateItem,
    MemCreateMembersRequest,
    DropdownResponse
} from "../../model";
import {getEditableRoles, getPrintableRoles} from "../../utils/rolePermissions";
import type {CheckboxGroupProps} from 'antd/es/checkbox';
import { debounce } from 'lodash';
import {reportService} from "../../services/domain/report/service";
import dayjs from "dayjs";
import {toROCDate} from "../../utils/DateUtil";
import RocDateInputPicker from "../../components/shared/RocDateInputPicker";
import { getPageSizeOptions, resetPaginationByTotal } from 'utils/pageUtil';

const {Title} = Typography;
const {Option} = Select;
const {Content} = Layout;

type MembershipListState = {
    formValues: any;
    memberships: MemSearchResponse[];
    selectedRowKeys: React.Key[];
    totalRecords: number;
    pagination: {
        current: number;
        pageSize: number;
    };
};

let membershipListStateCache: MembershipListState | null = null;

const MembershipPage: React.FC = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [createForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [memberships, setMemberships] = useState<MemSearchResponse[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [pdfViewerVisible, setPdfViewerVisible] = useState<boolean>(false);
    const [currentReportType, setCurrentReportType] = useState<string>("");
    const [reportTitle, setReportTitle] = useState<string>("");
    const [totalRecords, setTotalRecords] = useState(0);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [previewPdfQuestionModalVisible, setPreviewPdfQuestionModalVisible] = useState(false);
    const [identityTypeModalVisible, setIdentityTypeModalVisible] = useState(false);
    const [printLanguage, setPrintLanguage] = useState("ZH_TW");
    const [memberRegions, setMemberRegions] = useState<DropdownResponse[] | null>([]);
    const [memberLevelOptions, setMemberLevelOptions] = useState<DropdownResponse[] | null>([]);
    const [memberTypes, setMemberTypes] = useState<DropdownResponse[] | null>([]);
    const [fetchingMemberId, setFetchingMemberId] = useState(false);
    const [memberIdError, setMemberIdError] = useState<string | null>(null);
    const userAccount = localStorage.getItem('userAccount');
    const {hasEditPermission, hasPrintPermission} = useAuth();
    const location = useLocation();
    const identityTypeOptions= [
        {label: '負責人', value: 'RESPONSIBLE_PERSON'},
        {label: '董事長', value: 'CHAIRMAN'},
        {label: '總經理', value: 'GENERAL_MANAGER'},
        {label: '會籍聯絡人', value: 'MEMBERSHIP_CONTACT'},
        {label: '專委會聯絡人', value: 'COMMITTEE_CONTACT'},
        {label: '展覽會聯絡人', value: 'EXHIBITION_CONTACT'}
    ];
    
    const identityTypeOptions2: CheckboxGroupProps<string>['options'] = [
        {label: '會員代表', value: 'MEMBER_DELEGATE'},
        {label: '大會代表', value: 'CONGRESS_DELEGATE'}
    ];
    const [identityType, setIdentityType] = useState(identityTypeOptions[0]?.value || '');

    useEffect(() => {
        if (location.state?.restoreMembershipSearch && membershipListStateCache) {
            form.setFieldsValue(membershipListStateCache.formValues);
            setMemberships(membershipListStateCache.memberships);
            setSelectedRowKeys(membershipListStateCache.selectedRowKeys);
            setTotalRecords(membershipListStateCache.totalRecords);
            setPagination(membershipListStateCache.pagination);
            navigate(location.pathname, {replace: true});
        }
    }, [form, location.pathname, location.state, navigate]);

    // 處理預覽報表
    const handlePreviewReport = (reportType: string, title: string) => {
        // 檢查是否有選擇會員
        if (selectedRowKeys.length === 0) {
            message.warning('請至少選擇一個會員');
            return;
        }

        // 設置報表類型和標題
        setCurrentReportType(reportType);
        setReportTitle(title);

        // 顯示PDF預覽器
        setPdfViewerVisible(true);
    };

    // 關閉PDF預覽器
    const handleClosePdfViewer = () => {
        console.log('handleClosePdfViewer')
        setPdfViewerVisible(false);
    };

    // 處理範圍輸入框的變更，使起始值成為迄值的預設值
    const handleRangeStartChange = (fieldName: string, index: number, value: any) => {
        // 如果起始值是空值，迄值也要同時清除
        // 直接使用 setFields 更新表單屬性，確保當起始值有變化時，迄值也跟著更新
        const [, end] = form.getFieldValue(fieldName) || [];
        form.setFieldsValue({
            [fieldName]: [value, end || value],
        });
    };

    // 處理文本輸入範圍變化
    const handleTextRangeChange = debounce(
        (fieldName: string, e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            const index = e.target.dataset.index;

            if (index === 'start') {
                form.setFieldsValue({
                    [fieldName]: [value, value],
                });
            }
        },
        0.1
    );

    const handleTextRangeChange2 = (fieldName: string, e: any) => {
        handleRangeStartChange(fieldName, 0, e);
    };

    // 處理日期範圍變化
    const handleDateRangeChange = (fieldName: string, date: any) => {
        handleRangeStartChange(fieldName, 0, date);
    };

    // 載入下拉選單資料
    useEffect(() => {
        const fetchDropdownData = async () => {
            // 載入會員區域選單
            const regionData = await baseDataService.getDropdown({paramType: 'member_region'});
            setMemberRegions(regionData);

            // 載入會員類型選單
            const typeData = await baseDataService.getDropdown({paramType: 'member_type'});
            setMemberTypes(typeData);

            // 載入會員級數選單
            const memberLevelOptions = await baseDataService.getDropdown({paramType: 'member_level'});
            setMemberLevelOptions(memberLevelOptions);
        };

        fetchDropdownData();
    }, []);

    // 會員搜尋處理
    /* const handleSearch2 = async (values: any) => {
        console.log(JSON.stringify(values));
    } */

    const handleSearch = async (values: any) => {

        setSelectedRowKeys([]);
        setLoading(true);
        try {
            // 構建搜尋請求
            const searchRequest: MemSearchRequest = {
                memberIdStart: values.memberIdRange?.[0],
                memberIdEnd: values.memberIdRange?.[1],
                membershipLevelStart: values.membershipLevel?.[0],
                membershipLevelEnd: values.membershipLevel?.[1],
                capitalAmountStart: values.capitalAmount?.[0],
                capitalAmountEnd: values.capitalAmount?.[1],
                businessId: values.businessId,
                companyName: values.companyName,
                companyNameEn: values.companyNameEn,
                isSuspended: values.isSuspended === 'true' ? true :
                    values.isSuspended === 'false' ? false : undefined,
                isWithdrawaled: values.isWithdrawaled === 'true' ? true :
                    values.isWithdrawaled === 'false' ? false : undefined,
                joinDateStart: formatToAPIDate(values.joinDateRange?.[0]),
                joinDateEnd: formatToAPIDate(values.joinDateRange?.[1]),
                modifiedAtStart: values.modifiedAtRange?.[0]?.format('YYYY-MM-DD'),
                modifiedAtEnd: values.modifiedAtRange?.[1]?.format('YYYY-MM-DD'),
            };

            // 調用API搜尋會員
            const result = await membershipService.search(searchRequest);

            if (result) {
                setMemberships(result);
                setTotalRecords(result.length);
                // 重置到第一頁
                setPagination(resetPaginationByTotal(result.length));

                // 如果沒有返回數據，顯示通知
                if (result.length === 0) {
                    message.info('查無資料');
                }
            } else {
                // 如果返回的結果為null（可能是由於網絡錯誤或其他問題）
                setMemberships([]);
                setTotalRecords(0);
                message.error('查詢失敗，請稍後重試');
            }
        } catch (error) {
            console.error('搜尋會員失敗:', error);
            message.error('搜尋失敗，請稍後重試');
            setMemberships([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // 獲取新的 memberId
    const handleGetNextMemberId = async () => {
        try {
            const currentValues = createForm.getFieldsValue();
            const currentMemberRegion = currentValues.memberRegion;
            const currentMemberType = currentValues.memberType;

            // 檢查 memberRegion 和 memberType 是否都有值
            if (!currentMemberRegion || !currentMemberType) {
                return;
            }

            // 設置載入狀態
            setFetchingMemberId(true);
            setMemberIdError(null);

            // 調用 API 獲取新的 memberId
            const response = await baseDataService.getNextMemberIdPreview({
                memberRegion: currentMemberRegion,
                memberType: currentMemberType
            });

            // 如果成功獲取新的 memberId，更新表單
            if (response) {
                createForm.setFieldsValue({
                    memberId: response.memberId
                });
            }
        } catch (error) {
            console.error('Failed to get next member ID:', error);
            setMemberIdError('無法獲取新的會員編號，請稍後再試');
            message.error('無法獲取新的會員編號，請稍後再試');
        } finally {
            setFetchingMemberId(false);
        }
    };

    // 處理分頁、篩選和排序變更
    const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
        const newPagination = {
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize,
        };

        if (newPagination.pageSize !== pagination.pageSize) {
            setSelectedRowKeys([]);
        }
        setPagination(newPagination);
    };

    const saveMembershipListState = () => {
        membershipListStateCache = {
            formValues: form.getFieldsValue(true),
            memberships,
            selectedRowKeys,
            totalRecords,
            pagination,
        };
    };

    // 重置表單
    const handleReset = () => {
        form.resetFields();
        membershipListStateCache = null;
    };

    // 處理查看會員詳情
    const handleViewMember = (record: MemSearchResponse) => {
        console.log('Navigating to member detail:', record.membershipId);
        saveMembershipListState();
        navigate(`/membership/detail/${record.membershipId}`);
    };

    // 打開新增會員的對話窗
    const showCreateModal = () => {
        createForm.resetFields();
        setMemberIdError(null);
        setIsModalVisible(true);
    };
    // 判斷用哪個identityTypeOptions
    const getDefaultIdentityType = (reportType: string): string => {
    if (reportType === 'MembershipNametag' || reportType === 'exportMemberFaxCsv') {
        return identityTypeOptions[0]?.value || '';
    }
    return (identityTypeOptions2[0] as { value: string })?.value || '';
};
    // 打開新增會員的對話窗
    const handlePrint = (reportType: string, title: string) => {
        // 設置報表類型和標題
        setCurrentReportType(reportType);
        setReportTitle(title);
        setIdentityType(getDefaultIdentityType(reportType));
        switch (reportType) {
            case 'MembershipNametag': // 會員郵寄名條列表
                setIdentityTypeModalVisible(true);
                break;
            case 'MemberDelegate': // 會員代表名冊
                setIdentityTypeModalVisible(true);
                break;
            case 'MemberDelegateNametag': // 會員代表名條
                setIdentityTypeModalVisible(true);
                break;
            case 'exportMemberFaxCsv': // 會員傳真
                setIdentityTypeModalVisible(true);
                break;
            case 'MemberDelegateCard': // 會員代表卡
                setIdentityTypeModalVisible(true);
                break;
            default:
                setPreviewPdfQuestionModalVisible(true);
                break;
        }
    };

    // 關閉新增會員的對話窗
    const handleCancel = () => {
        setIsModalVisible(false);
    };

    // 提交新增會員的表單
    const handleCreate = async () => {
        try {
            const values = await createForm.validateFields();

            // 檢查必填欄位
            if (
                !values.memberType ||
                !values.memberRegion ||
                !values.memberId ||
                !values.companyName ||
                !values.businessId ||
                !values.capitalAmount ||
                !values.responsiblePerson
            ) {
                message.error('請填寫所有必填欄位');
                return;
            }

            setCreating(true);

            // 準備新增資料
            const newMember: MemberCreateItem = {
                memberId: values.memberId,
                companyName: values.companyName,
                joinDate: new Date().toISOString().split('T')[0], // 預設為今天
                membershipLevel: values.membershipLevel,
                createdBy: userAccount || 'system', // 使用登入帳號或預設值
                businessId: values.businessId, // 統一編號
                capitalAmount: values.capitalAmount, // 資本額
                responsiblePerson: values.responsiblePerson, // 負責人
            };

            // 調用 API 新增會員
            const createRequest: MemCreateMembersRequest = {
                members: [newMember]
            };

            const response = await membershipService.createMember(createRequest);

            if (response && response.createdCount > 0) {
                window.dispatchEvent(new Event('memberCountUpdate'));
                const memberId = values.memberId;
                // 一般會員格式為「區域 + 4 碼序號」，贊助會員格式為「A + 區域 + 3 碼序號」。
                const serialStartIndex = String(values.memberType).toUpperCase() === 'A' ? 2 : 1;
                const serialPart = memberId.substring(serialStartIndex);

                try {
                    // 更新會員 ID 序列
                    const serialUpdateResponse = await baseDataService.updateMemberIdSerial({
                        memberType: values.memberType,
                        memberRegion: values.memberRegion,
                        paramValue: serialPart
                    });

                    if (serialUpdateResponse && serialUpdateResponse.success) {
                        message.success('會員新增成功');

                        // 搜尋剛創建的會員以獲取 membershipId
                        const searchRequest: MemSearchRequest = {
                            memberIdStart: memberId,
                            memberIdEnd: memberId
                        };

                        const searchResult = await membershipService.search(searchRequest);

                        if (searchResult && searchResult.length > 0) {
                            // 關閉對話框
                            setIsModalVisible(false);

                            // 導航到會員詳情頁面並開啟編輯模式
                            navigate(`/membership/detail/${searchResult[0].membershipId}`, {
                                state: {isEditing: true}
                            });
                        } else {
                            // 如果找不到會員，只關閉對話框
                            setIsModalVisible(false);
                            // 刷新列表
                            handleSearch(form.getFieldsValue());
                        }
                    } else {
                        message.warning('會員已新增，但序列號更新失敗');
                        setIsModalVisible(false);
                        // 刷新列表
                        handleSearch(form.getFieldsValue());
                    }
                } catch (serialError) {
                    console.error('Update member ID serial failed:', serialError);
                    message.warning('會員已新增，但序列號更新失敗');
                    setIsModalVisible(false);
                    // 刷新列表
                    handleSearch(form.getFieldsValue());
                }
            } else {
                message.error('會員新增失敗');
            }
        } catch (error) {
            console.error('Create member failed:', error);
            message.error('會員新增失敗，請稍後再試');
        } finally {
            setCreating(false);
        }
    };

    // 表格列定義
    const columns = [
        {
            title: '會員證號',
            dataIndex: 'memberId',
            key: 'memberId'
        },
        {
            title: '統一編號',
            dataIndex: 'businessId',
            key: 'businessId'
        },
        {
            title: '公司名稱',
            dataIndex: 'companyName',
            key: 'companyName'
        },
        {
            title: '停權日期',
            dataIndex: 'suspensionDate',
            key: 'suspensionDate',
            render: (text: string) => text ? (
                <Tag color="red">{toROCDate(text)}</Tag>
            ) : null
        },
        {
            title: '退會日期',
            dataIndex: 'withdrawalDate',
            key: 'withdrawalDate',
            render: (text: string) => text ? (
                <Tag color="gray">{toROCDate(text)}</Tag>
            ) : null
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_: any, record: MemSearchResponse) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EyeOutlined/>}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewMember(record);
                        }}
                        title="查看詳情"
                    />
                </Space>
            )
        }
    ];

    const apiParam = useMemo(() => {
        console.log(`generateApiParam pdfViewerVisible: ${pdfViewerVisible}`)
        return {
            membershipIds: selectedRowKeys.map(key => ({membershipId: key.toString()})),
            language: printLanguage,
            identityType: identityType
        };
    }, [selectedRowKeys, printLanguage, identityType, pdfViewerVisible]);

    const handleIdentityTypeChange = ({target: {value}}: RadioChangeEvent) => {
        console.log('radio2 checked', value);
        setIdentityType(value);
    };

    const handleQueryBusinessInfo = async () => {
        const businessId = createForm.getFieldValue("businessId"); // 取得統一編號
        if (!businessId) {
            message.error("請輸入統一編號");
            return;
        }

        setCreating(true);

        try {

            const result = await membershipService.getBusinessInfo({
                businessId: businessId
            });
            if (result) {

                createForm.setFieldsValue({
                    companyName: result.companyName,
                    capitalAmount: result.capitalAmount,
                    responsiblePerson: result.responsiblePerson,
                });
            }

        } catch (error) {
            console.error('查詢公司基本資料失敗:', error);
            message.error('搜尋失敗，請稍後重試');
        } finally {
            setCreating(false);
        }
    }

    const getOptions = () => {
        if (currentReportType === 'MembershipNametag' || currentReportType === 'exportMemberFaxCsv') {
            return identityTypeOptions;
        }
        return identityTypeOptions2;
    }

    const formatToAPIDate = (date: dayjs.Dayjs | null) => {
        return date ? `${date.year()}-${(date.month() + 1).toString().padStart(2, '0')}-01` : undefined;
    };

    return (
        <Layout className="membership-page">
            <Content>
                {/* 頁面標題 */}
                <Row justify="center" className="mb-lg">
                    <Col>
                        <Title level={3} className="membership-title-with-emoji">
                            <span className="membership-title-emoji">👥</span> 會籍主檔
                        </Title>
                    </Col>
                </Row>

                {/* 搜尋表單 */}
                <Card
                    title={
                        <Space>
                            <SearchOutlined/>
                            <span>搜尋條件</span>
                        </Space>
                    }
                    className="membership-search-form"
                >
                    <Form
                        form={form}
                        name="membershipSearch"
                        onFinish={handleSearch}
                        layout="horizontal"
                        labelCol={{xs: {span: 8}, md: {span: 6}}}
                        wrapperCol={{xs: {span: 16}, md: {span: 18}}}
                        labelWrap
                    >
                        <Row gutter={[24, 0]}>
                            {/*'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs'*/}
                            {/* 會員證號範圍 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="會員證號" className="range-field">
                                    <Space.Compact>
                                        <Form.Item noStyle name={['memberIdRange', 0]}>
                                            <Input
                                                data-index="start"
                                                placeholder="起始值"
                                                onChange={(e) => handleTextRangeChange('memberIdRange', e)}
                                            />
                                        </Form.Item>
                                        <span className="range-separator">~</span>
                                        <Form.Item noStyle name={['memberIdRange', 1]}>
                                            <Input
                                                data-index="end"
                                                placeholder="結束值"
                                                onChange={(e) => handleTextRangeChange('memberIdRange', e)}
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>

                            {/* 統一編號範圍 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="統一編號" name="businessId">
                                    <Input
                                        placeholder="輸入統一編號"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[24, 0]}>
                            {/* 公司名稱 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="公司名稱" name="companyName">
                                    <Input
                                        placeholder="輸入公司名稱"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="公司英文名稱" name="companyNameEn">
                                    <Input
                                        placeholder="輸入公司名稱"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[24, 0]}>
                            {/* 入會日期範圍 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="入會日期"
                                           // name="joinDateRange"
                                           className="range-field"
                                >
                                    <Space.Compact>
                                        <Form.Item noStyle name={['joinDateRange', 0]}>
                                            <RocDateInputPicker
                                                mode="yyy-mm"
                                                onChange={(date: any) => handleDateRangeChange('joinDateRange', date)}
                                            />
                                        </Form.Item>
                                        <span className="range-separator">~</span>
                                        <Form.Item noStyle name={['joinDateRange', 1]}>
                                            <RocDateInputPicker
                                                mode="yyy-mm"
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>

                            {/* 異動日期範圍 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="異動日期" className="range-field">
                                    <Space.Compact>
                                        <Form.Item noStyle name={['modifiedAtRange', 0]}>
                                            <RocDateInputPicker
                                                onChange={(date: any) => handleDateRangeChange('modifiedAtRange', date)}
                                            />
                                        </Form.Item>
                                        <span className="range-separator">~</span>
                                        <Form.Item noStyle name={['modifiedAtRange', 1]}>
                                            <RocDateInputPicker />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[24, 0]}>
                            {/* 是否停權 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="是否停權" name="isSuspended">
                                    <Select placeholder="請選擇">
                                        <Option value="">請選擇</Option>
                                        <Option value="true">是</Option>
                                        <Option value="false">否</Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            {/* 是否退會 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="是否退會" name="isWithdrawaled">
                                    <Select placeholder="請選擇">
                                        <Option value="">請選擇</Option>
                                        <Option value="true">是</Option>
                                        <Option value="false">否</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[24, 0]}>
                            {/*'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs'*/}
                            {/* 會員級數範圍 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="會員級數" name="membershipLevel" className="range-field">
                                    <Space.Compact>
                                        <Form.Item
                                            name={['membershipLevel', 0]}
                                            noStyle
                                        >
                                            <Select
                                                placeholder="起始值"
                                                onChange={(e) => handleTextRangeChange2('membershipLevel', e)}
                                            >
                                                {memberLevelOptions?.map(level => (
                                                    <Option key={level.paramCode} value={level.paramCode}>
                                                        {level.paramValue}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                        <span className="range-separator">~</span>
                                        <Form.Item
                                            name={['membershipLevel', 1]}
                                            noStyle
                                        >
                                            <Select
                                                placeholder="結束值"
                                            >
                                                {memberLevelOptions?.map(level => (
                                                    <Option key={level.paramCode} value={level.paramCode}>
                                                        {level.paramValue}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>

                            {/* 資本額範圍 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="資本額 (元)" className="range-field">
                                    <Space.Compact>
                                        <Form.Item noStyle name={['capitalAmount', 0]}>
                                            <Input
                                                placeholder="起始值"
                                                data-index="start"
                                                onChange={(e) => handleTextRangeChange('capitalAmount', e)}
                                            />
                                        </Form.Item>
                                        <span className="range-separator">~</span>
                                        <Form.Item noStyle name={['capitalAmount', 1]}>
                                            <Input
                                                placeholder="結束值"
                                                data-index="end"
                                                onChange={(e) => handleTextRangeChange('capitalAmount', e)}
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[24, 0]} justify="center">
                            {/* 按鈕區域 */}
                            <Col xs={24}>
                                <Form.Item wrapperCol={{span: 24}}>
                                    <div className="form-button-container">
                                        <Button
                                            onClick={handleReset}
                                            icon={<DeleteOutlined/>}
                                        >
                                            清除
                                        </Button>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            icon={<SearchOutlined/>}
                                        >
                                            查詢
                                        </Button>
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>

                {/* 搜尋結果區域 */}
                <Card
                    title={
                        <>
                            <Content>
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Title level={5}>搜尋結果: {totalRecords} 筆記錄</Title>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined/>}
                                                onClick={showCreateModal}
                                                disabled={!hasEditPermission(getEditableRoles(location.pathname))}

                                            >
                                                新增會員
                                            </Button>
                                            <Dropdown
                                                menu={{
                                                    items: [
                                                        {key: '1', label: '全會員證書', icon: (<PrinterOutlined/>)},
                                                        {key: '2', label: '會員資料', icon: (<PrinterOutlined/>)},
                                                        {key: '3', label: '會員產品列表', icon: (<PrinterOutlined/>)},
                                                        {
                                                            key: '4',
                                                            label: '會員基本資料列表',
                                                            icon: (<PrinterOutlined/>)
                                                        },
                                                        {
                                                            key: '5',
                                                            label: '會員郵寄名條列表',
                                                            icon: (<PrinterOutlined/>)
                                                        },
                                                        {key: '6', label: '會員公司名冊', icon: (<PrinterOutlined/>)},
                                                        {key: '7', label: '會員工廠名冊', icon: (<PrinterOutlined/>)},
                                                        {
                                                            key: '8',
                                                            label: '會員代表名冊',
                                                            icon: (<PrinterOutlined/>)
                                                        },
                                                        {
                                                            key: '9',
                                                            label: '會員代表名條',
                                                            icon: (<PrinterOutlined/>)
                                                        },
                                                        {
                                                            key: '10',
                                                            label: '會員網址、電子郵件列表',
                                                            icon: (<PrinterOutlined/>)
                                                        },
                                                        {
                                                            key: '11',
                                                            label: '會員入會日期列表',
                                                            icon: (<PrinterOutlined/>)
                                                        },
                                                        {
                                                            key: '12',
                                                            label: '停權會員列表',
                                                            icon: (<PrinterOutlined/>)
                                                        },
                                                        {
                                                            key: '13',
                                                            label: '退會會員列表',
                                                            icon: (<PrinterOutlined/>)
                                                        },
                                                        {
                                                            key: '14',
                                                            label: '會員代表卡',
                                                            icon: (<PrinterOutlined/>)
                                                        },
                                                        {
                                                            key: '15',
                                                            label: '會員傳真',
                                                            icon: (<DownloadOutlined/>)
                                                        }
                                                    ],
                                                    onClick: ({key}) => {
                                                        switch (key) {
                                                            case '1':
                                                                handlePreviewReport('MembershipPaper', '全會員證書');
                                                                break;
                                                            case '2':
                                                                handlePreviewReport('Membership', '會員資料');
                                                                break;
                                                            case '3':
                                                                handlePreviewReport('MemberProducts', '會員產品列表');
                                                                break;
                                                            case '4':
                                                                handlePrint('MembershipInfo', '會員基本資料列表');
                                                                break;
                                                            case '5':
                                                                handlePrint('MembershipNametag', '會員郵寄名條列表');
                                                                break;
                                                            case '6':
                                                                handlePrint('MemberCompany', '會員公司名冊');
                                                                break;
                                                            case '7':
                                                                handlePrint('MemberFactory', '會員工廠名冊');
                                                                break;
                                                            case '8':
                                                                handlePrint('MemberDelegate', '會員代表名冊');
                                                                break;
                                                            case '9':
                                                                handlePrint('MemberDelegateNametag', '會員代表名條');
                                                                break;
                                                            case '10':
                                                                handlePreviewReport('MembershipWebsiteEmail', '會員網址、電子郵件列表');
                                                                break;
                                                            case '11':
                                                                handlePreviewReport('MembershipJoinDate', '會員入會日期列表');
                                                                break;
                                                            case '12':
                                                                handlePreviewReport('MembershipSuspension', '停權會員列表');
                                                                break;
                                                            case '13':
                                                                handlePreviewReport('MembershipWithdrawal', '退會會員列表');
                                                                break;
                                                            case '14':
                                                                handlePrint('MemberDelegateCard', '會員代表卡');
                                                                break;
                                                            case '15':
                                                                handlePrint('exportMemberFaxCsv', '會員傳真');
                                                        }
                                                    },
                                                }}
                                                disabled={selectedRowKeys.length === 0}
                                                trigger={['click']}
                                            >
                                                <Button icon={<DownOutlined/>}
                                                        disabled={
                                                            selectedRowKeys.length === 0 || !hasPrintPermission(getPrintableRoles(location.pathname))
                                                        }
                                                >
                                                    列印選項
                                                </Button>
                                            </Dropdown>
                                        </Space>
                                    </Col>
                                </Row>
                            </Content>
                        </>
                    }
                >
                    <div className="table-responsive responsive-table">
                        <Table
                            virtual
                            rowKey="membershipId"
                            dataSource={memberships}
                            columns={columns}
                            loading={loading}
                            scroll={{ y: 600 }}
                            rowClassName={(record) => record.suspensionDate ? 'suspended-row' : (record.withdrawalDate ? 'withdrawaled-row' : '')}
                            pagination={{
                                ...pagination,
                                total: totalRecords,
                                showSizeChanger: true,
                                pageSizeOptions: getPageSizeOptions(totalRecords),
                                showTotal: (total) => `共 ${total} 筆，已選 ${selectedRowKeys.length} 筆`
                            }}
                            onChange={handleTableChange}
                            rowSelection={{
                                type: 'checkbox',
                                selectedRowKeys,
                                onChange: (selectedKeys) => {
                                    setSelectedRowKeys(selectedKeys);
                                }
                            }}
                        />
                    </div>
                </Card>

                {/* 新增會員對話窗 */}
                <Modal
                    title="新增會員"
                    open={isModalVisible}
                    onCancel={handleCancel}
                    footer={[
                        <Button key="cancel" onClick={handleCancel}>
                            取消
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            loading={creating}
                            onClick={handleCreate}
                        >
                            新增
                        </Button>
                    ]}
                >
                    <Form
                        form={createForm}
                        layout="vertical"
                        labelWrap
                    >
                        <Row gutter={[24, 0]}>
                            <Col xs={24}>
                                {/* 會員證號複合元件 */}
                                <Form.Item
                                    label="會員證號"
                                    required
                                    validateStatus={memberIdError ? 'error' : ''}
                                    help={memberIdError}
                                >
                                    <Space.Compact style={{width: '100%'}}>
                                        <Form.Item
                                            name="memberType"
                                            noStyle
                                            rules={[{required: true, message: '請選擇會員類型'}]}
                                        >
                                            <Select
                                                style={{width: '30%'}}
                                                placeholder="類型"
                                                onChange={handleGetNextMemberId}
                                            >
                                                {memberTypes?.map(type => (
                                                    <Option key={type.paramCode} value={type.paramCode}>
                                                        {type.paramValue}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                        <Form.Item
                                            name="memberRegion"
                                            noStyle
                                            rules={[{required: true, message: '請選擇會員區域'}]}
                                        >
                                            <Select
                                                style={{width: '30%'}}
                                                placeholder="區域"
                                                onChange={handleGetNextMemberId}
                                            >
                                                {memberRegions?.map(region => (
                                                    <Option key={region.paramCode} value={region.paramCode}>
                                                        {region.paramValue}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                        <Form.Item
                                            name="memberId"
                                            noStyle
                                            rules={[{required: true, message: '請輸入會員編號'}]}
                                        >
                                            <Input
                                                style={{width: '40%'}}
                                                placeholder="編號"
                                                disabled={true}
                                                suffix={fetchingMemberId ?
                                                    <span style={{color: '#1890ff'}}>載入中...</span> : null}
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="統一編號"
                                    name="businessId"
                                    rules={[{required: true, message: '輸入統一編號'}]}
                                >
                                    <Space.Compact>
                                        <Input
                                            placeholder="輸入統一編號"
                                        />
                                        <Button
                                            type="primary"
                                            loading={creating}
                                            onClick={handleQueryBusinessInfo}
                                        >
                                            查詢
                                        </Button>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                {/* 公司名稱 */}
                                <Form.Item
                                    label="公司名稱"
                                    name="companyName"
                                    rules={[{required: true, message: '輸入公司名稱'}]}
                                >
                                    <Input placeholder="請輸入公司名稱" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="資本額 (元)"
                                    name="capitalAmount"
                                    rules={[{required: true, message: '輸入資本額'}]}
                                >
                                    <Input placeholder="資本額" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                {/* 公司名稱 */}
                                <Form.Item
                                    label="負責人"
                                    name="responsiblePerson"
                                    rules={[{required: true, message: '輸入負責人'}]}
                                >
                                    <Input placeholder="負責人" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Modal>

                <Modal
                    title="請選擇列印中文或英文"
                    open={previewPdfQuestionModalVisible}
                    onCancel={() => {
                        setPrintLanguage("EN_US");
                        setPreviewPdfQuestionModalVisible(false)
                        console.log('onCancel')
                        // 顯示PDF預覽器
                        setPdfViewerVisible(true);
                    }}
                    onOk={() => {
                        setPrintLanguage("ZH_TW");
                        setPreviewPdfQuestionModalVisible(false)
                        console.log('onOk')
                        // 顯示PDF預覽器
                        setPdfViewerVisible(true);
                    }}
                    okText="中文"
                    cancelText="英文"
                >
                </Modal>

                <Modal
                    title="請選擇身份別"
                    open={identityTypeModalVisible}
                    onCancel={() => {
                        setIdentityType(getDefaultIdentityType(currentReportType));
                        setIdentityTypeModalVisible(false)
                    }}
                    onOk={() => {
                        if( currentReportType === 'exportMemberFaxCsv') {
                            reportService.exportMemberFaxCsv({
                                membershipIds: apiParam.membershipIds,
                                type: apiParam.identityType
                            });
                        } else {
                            if (currentReportType === 'MembershipNametag') {
                                setPreviewPdfQuestionModalVisible(true);
                            } else {
                                setPdfViewerVisible(true);
                            }
                        }
                        setIdentityTypeModalVisible(false)
                    }}
                >
                    <Radio.Group options={getOptions()}
                                 onChange={handleIdentityTypeChange} value={identityType}/>
                </Modal>
            </Content>

            {/* PDF預覽器組件 */}
            {
                pdfViewerVisible && (
                    <MembershipPdfViewer
                        visible={pdfViewerVisible}
                        onClose={handleClosePdfViewer}
                        reportType={currentReportType}
                        apiParam={apiParam}
                        title={reportTitle}
                    />
                )
            }
        </Layout>
    );
};

export default MembershipPage;
